import React from 'react';
import { useLang } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Check, X, Trophy } from 'lucide-react';

const featureKeys = ['dsgvo', 'server_de', 'activesync', 'calendar', 'cloud', 'office', 'antispam', 'smime', 'price', 'open_source'];

function CellValue({ val, isZimbra }) {
  if (val === true) return (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center mx-auto ${isZimbra ? 'bg-neon/20' : 'bg-white/5'}`}>
      <Check className={`w-4 h-4 ${isZimbra ? 'text-neon' : 'text-zinc-500'}`} />
    </div>
  );
  if (val === false) return (
    <div className="w-7 h-7 rounded-full flex items-center justify-center mx-auto bg-red-500/10">
      <X className="w-4 h-4 text-red-400/50" />
    </div>
  );
  return <span className={`text-sm font-bold ${isZimbra ? 'text-neon' : 'text-zinc-400'}`}>{val}</span>;
}

export default function ComparisonTable() {
  const { t } = useLang();

  return (
    <section id="comparison" data-testid="comparison-section" className="py-24 lg:py-32 bg-surface relative overflow-hidden lg:skew-section">
      {/* Ambient glow */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-cyan/5 rounded-full blur-[200px]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-cyan mb-4 bg-cyan/10 border border-cyan/20 rounded-full px-4 py-1.5 mx-auto">
            <Trophy className="w-3.5 h-3.5" />
            {t('comparison.overline')}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight text-white mb-4">
            {t('comparison.title')}
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 font-light max-w-2xl mx-auto">
            {t('comparison.subtitle')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="overflow-x-auto rounded-2xl border border-white/[0.06] bg-[#0a0a0f]/80 backdrop-blur-sm"
        >
          <table className="w-full min-w-[550px]" data-testid="comparison-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-5 px-6 text-sm font-bold uppercase tracking-wider text-zinc-600 w-1/4">
                  {t('comparison.headers.feature')}
                </th>
                <th className="py-5 px-6 text-center w-1/4">
                  <div className="inline-flex items-center gap-2 bg-neon text-black font-black text-sm uppercase tracking-wider px-5 py-2 rounded-full shadow-[0_0_20px_rgba(212,255,0,0.3)]">
                    {t('comparison.zimbra')}
                  </div>
                </th>
                <th className="py-5 px-6 text-center text-sm font-bold uppercase tracking-wider text-zinc-600 w-1/4">
                  Gmail / Workspace
                </th>
                <th className="py-5 px-6 text-center text-sm font-bold uppercase tracking-wider text-zinc-600 w-1/4">
                  Microsoft 365
                </th>
              </tr>
            </thead>
            <tbody>
              {featureKeys.map((fk, i) => (
                <tr
                  key={fk}
                  className={`border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors ${
                    i % 2 === 0 ? 'bg-white/[0.01]' : ''
                  }`}
                >
                  <td className="py-4 px-6 text-sm text-zinc-300 font-medium">
                    {t(`comparison.headers.${fk}`)}
                  </td>
                  <td className="py-4 px-6 text-center bg-neon/[0.03]">
                    <CellValue val={t(`comparison.zimbra_vals.${fk}`)} isZimbra={true} />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <CellValue val={t(`comparison.gmail_vals.${fk}`)} isZimbra={false} />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <CellValue val={t(`comparison.outlook_vals.${fk}`)} isZimbra={false} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </div>
    </section>
  );
}
